<template>
  <transition name="fading">
    <Loading v-if="isLoading" />
  </transition>

  <div class="admin-page wide">
    <div class="page-hero">
      <div>
        <p class="page-kicker">State Benchmark</p>
        <h1 class="page-title">Clinic Comparison</h1>
        <p class="page-subtitle">Compare clinics across a state using canonical state filtering.</p>
      </div>
    </div>

    <div class="toolbar">
      <div class="toolbar-search">
        <div class="filter-grid">
          <div class="form-section">
            <label class="field-label">State</label>
            <select class="ui-select" v-model="filters.state" @change="loadAll">
              <option value="">Choose state</option>
              <option v-for="state in states" :key="state" :value="state">{{ state }}</option>
            </select>
          </div>
          <div class="form-section">
            <label class="field-label">Start date</label>
            <input type="date" class="ui-input" v-model="filters.start_date" @change="loadAll">
          </div>
          <div class="form-section">
            <label class="field-label">End date</label>
            <input type="date" class="ui-input" v-model="filters.end_date" @change="loadAll">
          </div>
        </div>
      </div>
    </div>

    <div v-if="pageError" class="alert-message">
      <i class="fas fa-exclamation-circle"></i> {{ pageError }}
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <span class="stat-label">Clinics</span>
        <strong class="stat-value">{{ summary?.totals?.clinics ?? 0 }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Active clinics</span>
        <strong class="stat-value">{{ activeClinicCount }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Screenings</span>
        <strong class="stat-value">{{ formatNumber(summary?.totals?.screening_volume ?? 0) }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Unique patients</span>
        <strong class="stat-value">{{ formatNumber(summary?.totals?.unique_patients_screened ?? 0) }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Abnormal rate</span>
        <strong class="stat-value stat-text">{{ formatRate(summary?.totals?.abnormal_rate) }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Grade 2 rate</span>
        <strong class="stat-value stat-text">{{ formatRate(stateGrade2Rate) }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Avg quality %</span>
        <strong class="stat-value stat-text">{{ formatNullableNumber(summary?.totals?.avg_image_quality) }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Quality risk</span>
        <strong class="stat-value">{{ qualityRiskCount }}</strong>
      </div>
    </div>

    <div class="table-card">
      <table class="ui-table mobile-card-table">
        <thead>
          <tr>
            <th class="text-start">Clinic</th>
            <th class="text-start">Screenings</th>
            <th class="text-start">Unique patients</th>
            <th class="text-start">Abnormal rate</th>
            <th class="text-start">Grade 2 rate</th>
            <th class="text-start">Avg quality %</th>
            <th class="text-start">Growth</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in clinics" :key="row.location_uuid" class="clickable-row" @click="openClinicDetail(row)">
            <td data-label="Clinic">
              <span class="table-primary">{{ row.clinic_name || 'Unnamed clinic' }}</span>
              <span class="table-secondary">{{ row.location_uuid }}</span>
            </td>
            <td data-label="Screenings">
              <span class="table-primary">{{ row.metrics?.screening_volume ?? 0 }}</span>
              <span class="table-secondary">{{ row.metrics?.active_days ?? 0 }} active days</span>
            </td>
            <td data-label="Patients">
              <span class="table-primary">{{ row.metrics?.unique_patients_screened ?? 0 }}</span>
              <span class="table-secondary">Patients screened</span>
            </td>
            <td data-label="Abnormal">
              <span class="table-primary">{{ formatRate(row.metrics?.abnormal_rate) }}</span>
              <span class="table-secondary">{{ row.metrics?.abnormal_cases ?? 0 }} cases</span>
            </td>
            <td data-label="Grade 2">
              <span class="table-primary">{{ formatRate(row.metrics?.grade_2_rate) }}</span>
              <span class="table-secondary">{{ row.metrics?.grade_2_cases ?? 0 }} cases</span>
            </td>
            <td data-label="Quality">
              <span class="table-primary">{{ formatNullableNumber(row.metrics?.avg_image_quality) }}</span>
              <span class="table-secondary">Image quality</span>
            </td>
            <td data-label="Growth">
              <span class="table-primary">{{ formatGrowth(row.metrics?.growth_rate) }}</span>
              <span class="table-secondary">Prev {{ row.metrics?.previous_screening_volume ?? 0 }}</span>
            </td>
          </tr>
          <tr v-if="clinics.length === 0">
            <td colspan="7">
              <div class="empty-state">
                <strong>No clinics to show.</strong>
                <p>Select a state and date range.</p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="selectedClinicDetail || isDetailLoading" class="detail-panel">
      <div class="section-heading">
        <div>
          <h2 class="section-title">{{ selectedClinicDetail?.clinic_name || 'Loading clinic detail' }}</h2>
          <p class="section-description">
            {{ selectedClinicDetail?.state || filters.state }} clinic performance for the selected period.
          </p>
        </div>
        <button type="button" class="icon-button-soft" @click="selectedClinicDetail = null" title="Close detail">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div v-if="isDetailLoading" class="empty-state">Loading clinic detail...</div>
      <div v-else class="detail-grid">
        <div class="stat-card">
          <span class="stat-label">Screenings</span>
          <strong class="stat-value">{{ selectedClinicDetail.metrics?.screening_volume ?? 0 }}</strong>
        </div>
        <div class="stat-card">
          <span class="stat-label">Unique patients</span>
          <strong class="stat-value">{{ selectedClinicDetail.metrics?.unique_patients_screened ?? 0 }}</strong>
        </div>
        <div class="stat-card">
          <span class="stat-label">Active days</span>
          <strong class="stat-value">{{ selectedClinicDetail.metrics?.active_days ?? 0 }}</strong>
        </div>
        <div class="stat-card">
          <span class="stat-label">Abnormal cases</span>
          <strong class="stat-value">{{ selectedClinicDetail.metrics?.abnormal_cases ?? 0 }}</strong>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from '@/components/Loading.vue';
import CANONICAL_STATES from '@/constants/states';
import { getAdminStateClinicDetail, getAdminStateClinics, getAdminStateSummary } from '@/services/comparisonServices';

function toISODate(inputDate) {
  const date = inputDate instanceof Date ? inputDate : new Date(inputDate);
  return date.toISOString().slice(0, 10);
}

export default {
  name: 'StateComparison',
  components: {
    Loading,
  },
  data() {
    const today = new Date();
    const endDate = toISODate(today);
    const startDate = toISODate(new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000));

    return {
      states: CANONICAL_STATES,
      filters: {
        state: '',
        start_date: startDate,
        end_date: endDate,
      },
      summary: null,
      clinics: [],
      selectedClinicDetail: null,
      isLoading: false,
      isDetailLoading: false,
      pageError: '',
    };
  },
  computed: {
    activeClinicCount() {
      return this.clinics.filter((row) => (row.metrics?.screening_volume || 0) > 0).length;
    },
    qualityRiskCount() {
      return this.clinics.filter((row) => row.metrics?.avg_image_quality !== null && row.metrics?.avg_image_quality < 0.65).length;
    },
    stateGrade2Rate() {
      const totalScreenings = this.clinics.reduce((total, row) => total + (row.metrics?.screening_volume || 0), 0);
      const grade2Cases = this.clinics.reduce((total, row) => total + (row.metrics?.grade_2_cases || 0), 0);
      return totalScreenings > 0 ? grade2Cases / totalScreenings : 0;
    },
  },
  methods: {
    formatNumber(value) {
      return Number(value || 0).toLocaleString();
    },
    formatRate(value) {
      if (value === null || value === undefined) return '0%';
      return `${(Number(value) * 100).toFixed(1)}%`;
    },
    formatGrowth(value) {
      if (value === null || value === undefined) return '-';
      return `${(Number(value) * 100).toFixed(1)}%`;
    },
    formatNullableNumber(value) {
      if (value === null || value === undefined) return '-';
      return `${(Number(value) * 100).toFixed(0)}%`;
    },
    async openClinicDetail(row) {
      this.isDetailLoading = true;
      this.selectedClinicDetail = {
        clinic_name: row.clinic_name,
        state: row.state,
        metrics: row.metrics || {},
      };
      try {
        const result = await getAdminStateClinicDetail({
          location_uuid: row.location_uuid,
          start_date: this.filters.start_date,
          end_date: this.filters.end_date,
        });
        this.selectedClinicDetail = result.data || this.selectedClinicDetail;
      } catch (error) {
        this.pageError = error.response?.data?.detail || 'Unable to load clinic detail.';
      } finally {
        this.isDetailLoading = false;
      }
    },
    async loadAll() {
      this.pageError = '';
      if (!this.filters.state) {
        this.summary = null;
        this.clinics = [];
        return;
      }

      this.isLoading = true;
      try {
        const [summaryResult, clinicsResult] = await Promise.all([
          getAdminStateSummary(this.filters),
          getAdminStateClinics({ ...this.filters, sort: 'screening_volume', order: 'desc' }),
        ]);
        this.summary = summaryResult.data || null;
        this.clinics = clinicsResult.data || [];
        this.selectedClinicDetail = null;
      } catch (error) {
        this.pageError = error.response?.data?.detail || 'Unable to load state benchmark data.';
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.filter-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
}

.stats-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.stat-text {
  font-size: 1rem;
}

.clickable-row {
  cursor: pointer;
}

.detail-panel {
  margin-top: 18px;
  padding: 18px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius);
  background: #ffffff;
  box-shadow: var(--ui-shadow);
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

@media (max-width: 960px) {
  .filter-grid {
    grid-template-columns: 1fr;
  }

  .stats-grid,
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
